import { useFundTrades } from '@/hooks/useFundTrades';
import { useAppQueryParams } from '@/hooks/useAppQueryParams';
import { getCurrencyDisplayClass, getCurrencyString, getTimeFromMilitaryTime } from '@/lib/formatting';
import { ApiError } from '@/lib/errors';

/**
 * Port of fund-trades.component. The Angular version put up a full-screen
 * LoadingController overlay while it fetched; an inline row is less jarring
 * when you are expanding one fund inside a long list.
 */
export function FundTrades({ fundId }: { fundId: string }) {
  const { activityDate, tenant } = useAppQueryParams();
  // Note: read through the query object rather than destructuring `data`.
  // TanStack Query narrows `data` off isPending/isError, and destructuring
  // discards that correlation, leaving `data` possibly undefined.
  const tradesQuery = useFundTrades({
    fundId,
    tradeDateAsYyyyMmDd: activityDate,
    tenant,
  });

  if (tradesQuery.isPending) {
    return <p className="px-4 py-3 text-sm text-gray1">Loading trades...</p>;
  }

  if (tradesQuery.isError) {
    return (
      <p role="alert" className="px-4 py-3 text-sm text-money-negative">
        {tradesQuery.error instanceof ApiError
          ? tradesQuery.error.userMessage
          : 'Could not load trades.'}
      </p>
    );
  }

  const trades = tradesQuery.data;

  if (trades.length === 0) {
    return <p className="px-4 py-3 text-sm text-gray1">No trades for this fund.</p>;
  }

  return (
    <div className="bg-slate-50 px-2 py-2 text-sm">
      <div className="grid grid-cols-12 gap-1 border-b border-slate-200 px-2 py-1.5 font-semibold text-slate-700 md:gap-2">
        <div className="hidden md:col-span-2 md:block" />
        <div className="col-span-5 md:col-span-4">
          Intermediary <span className="hidden font-normal text-gray1 md:inline">(and Dealer)</span>
        </div>
        <div className="col-span-4 text-right md:col-span-2">Amount</div>
        <div className="col-span-3 text-right md:col-span-2">
          <span className="lg:hidden">Rcvd</span>
          <span className="hidden lg:inline">Received at</span>
        </div>
      </div>

      {trades.map((trade) => (
        <div
          key={trade.tradeId}
          className="grid grid-cols-12 items-center gap-1 px-2 py-1.5 text-slate-800 md:gap-2"
        >
          <div className="hidden md:col-span-2 md:block" />
          <div className="col-span-5 md:col-span-4">
            {trade.intermediaryName}
            {trade.dealerName && (
              <span className="hidden pl-1.5 text-gray1 md:inline">
                (Dealer: {trade.dealerName})
              </span>
            )}
          </div>
          <div className="col-span-4 text-right md:col-span-2">
            <span className={`${getCurrencyDisplayClass(trade.amount)} whitespace-nowrap tabular-nums`}>
              {getCurrencyString(trade.amount)}
            </span>
          </div>
          <div className="col-span-3 text-right md:col-span-2">
            {getTimeFromMilitaryTime(trade.tradeTime)}
          </div>
        </div>
      ))}
    </div>
  );
}
